import { Service, inject } from '@angular/core';
import {
  CanActivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { DrupalService } from '@app/services/drupal.service';
import { Observable, of, from } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Service()
export class DrupalRouteGuardService {
  #drupal = inject(DrupalService);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // Route data is keyed by the pathname. Query parameters and fragments
    this.#drupal.setQueryParams(state.url);

    // belong to browser state and must never trigger a Drupal route fetch.
    const plainUrl = state.url.split(/[?#]/, 1)[0] || '/';

    // Promise-based route loading cannot be cancelled by the router. The ID
    // lets the services discard a late result from a superseded navigation.
    const navigationId = this.#drupal.beginRouteNavigation();

    // The SSR route is already available before the first client render. Reuse
    // it and only hydrate shell data that remains in Angular's HTTP cache.
    if (this.#drupal.consumeInitialRoute(plainUrl)) {
      return from(this.#drupal.hydrateInitialRoute(plainUrl, navigationId)).pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error hydrating initial route:', err);
          return of(true);
        })
      );
    }

    this.#drupal.routeHandled.set(false);
    this.#drupal.route.set(null);

    return from(this.#drupal.resolveRoute(plainUrl, navigationId)).pipe(
      map(() => {
        return true;
      }),
      catchError((err) => {
        console.error('Error resolving route:', err);
        return of(true);
      })
    );
  }
}

export const drupalRouteGuard: CanActivateFn = (route, state) => {
  return inject(DrupalRouteGuardService).canActivate(route, state);
};
